import { useCallback, useMemo } from "react";
import {
  FixedMixDefaultsPreview,
  FixedMixDefaultsRequest,
  usePreviewFixedMixDefaultsMutation,
} from "store/api/generatedApi";
import { useTenant } from "hooks/settings";
import { useDebouncedRequest, PendingRequest } from "hooks/useDebouncedRequest";

type PreviewRequest = (
  request: FixedMixDefaultsRequest
) => PendingRequest<FixedMixDefaultsPreview>;

export const useFixedMixPreviewState = ({
  request,
  tenantName,
  check,
}: {
  request: FixedMixDefaultsRequest | null;
  tenantName: string;
  check: PreviewRequest;
}) => {
  const input = useMemo(
    () => (request === null ? null : { request, tenantName }),
    [request, tenantName]
  );
  const checkPreview = useCallback(
    (input: { request: FixedMixDefaultsRequest; tenantName: string }) =>
      check(input.request),
    [check]
  );
  const { completion, isCurrent, ...status } = useDebouncedRequest({
    request: input,
    check: checkPreview,
    delay: 300,
  });
  return {
    ...status,
    preview:
      isCurrent && completion?.status === "success"
        ? completion.response
        : null,
  };
};

export const useFixedMixPreview = (request: FixedMixDefaultsRequest | null) => {
  const tenantName = useTenant();
  const [preview] = usePreviewFixedMixDefaultsMutation();
  const check = useCallback<PreviewRequest>(
    (request) => {
      const pending = preview({ tenantName, fixedMixDefaultsRequest: request });
      return {
        response: pending.unwrap(),
        abort: () => {
          pending.abort();
        },
      };
    },
    [preview, tenantName]
  );
  return useFixedMixPreviewState({ request, tenantName, check });
};
